import {Scope} from '@wix/app-settings-client';
import {IWidgetControllerConfig} from '@wix/native-components-infra';
import {I$WWrapper} from '@wix/native-components-infra/dist/es/src/types/types';
import {createSlotVeloAPIFactory} from '@wix/widget-plugins-ooi/velo';
import {withErrorReporting} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/errorReporter';
import {CreateControllerFn} from '@wix/yoshi-flow-editor';
import _ from 'lodash';
import {ProductPagePanoramaTransactions, UserInputType} from '../constants';
import {wixCodeGetProduct} from '../services/getProduct';
import {IProductPageControllerConfig} from '../types/app-types';
import {
  OnChoiceSelectedCallbackType,
  OnQuantityChangedCallbackType,
  OverrideCallback,
  ProductPageStore,
} from './ProductPageStore';
import {SPECS} from '../specs';

type ResolveFn<T> = (value: T | PromiseLike<T>) => void;
export type ReportError = (e: string | Error, shouldRethrowException?: boolean) => any;

export const ProductPageController: CreateControllerFn = (controllerParams) => {
  const {controllerConfig, flowAPI} = controllerParams;
  const {setProps} = controllerConfig;
  const siteStore = controllerParams.appData.context.siteStore;
  let onReadyResolver: ResolveFn<void>;

  let productPageStore: ProductPageStore;
  let config: IProductPageControllerConfig = _.clone(controllerConfig.config);

  const waitUntilReady = new Promise<void>((resolve) => {
    onReadyResolver = resolve;
  });

  const controllerConfigRef: IWidgetControllerConfig = controllerConfig;

  const exports = () =>
    withErrorReporting(flowAPI.reportError)({
      getProduct: async () => {
        await waitUntilReady;
        const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeGetProduct;
        flowAPI.panoramaClient.transaction(panoramaInteraction).start();
        const onSuccess = () => {
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        };
        return wixCodeGetProduct(siteStore, flowAPI.reportError, onSuccess);
      },
      // @deprecated - New method is getCustomText
      getCustomTextFieldsValues: (): string[] => {
        const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeGetCustomTextFieldValues;
        flowAPI.panoramaClient.transaction(panoramaInteraction).start();
        const customTextFieldsValues = productPageStore.userInputs.text;
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        return customTextFieldsValues;
      },
      ...(siteStore.experiments.enabled(SPECS.PRODUCT_PAGE_NEW_WIX_CODE_API) && {
        // eslint-disable-next-line sonarjs/no-identical-functions
        getCustomText: async (): Promise<string[]> => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeGetCustomText;
          flowAPI.panoramaClient.transaction(panoramaInteraction).start();
          const customTextFieldsValues = productPageStore.customText;
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
          return customTextFieldsValues;
        },
        getQuantity: async () => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeGetQuantity;
          const selectedQuantity = productPageStore.selectedQuantity;
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
          return selectedQuantity;
        },
        setQuantity: async (value: number) => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeSetQuantity;
          productPageStore.handleUserInput(UserInputType.Quantity, value, 0);
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        },
        onQuantityChanged: async (callback: OnQuantityChangedCallbackType) => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeOnQuantityChanged;
          productPageStore.setOnQuantityChangedCallback(callback);
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        },
        getSelectedVariantId: async () => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeGetSelectedVariantId;
          const selectedVariantId = productPageStore.selectedVariant?.id;
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
          return selectedVariantId;
        },
        getSelectedChoices: async () => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeGetSelectedChoices;
          const selectedChoices = productPageStore.selectedChoices;
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
          return selectedChoices;
        },
        onChoiceSelected: async (callback: OnChoiceSelectedCallbackType) => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeOnChoiceSelected;
          productPageStore.setOnChoiceSelectedCallback(callback);
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        },
        onAddToCart: async (callback: OverrideCallback) => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeOnAddToCart;
          productPageStore.setOnAddToCartCallback(callback);
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        },
        setAddToCartLabel: async (text: string) => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeOnAddToCart;
          productPageStore.setAddToCartTextOverride(text);
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        },
        onBuyNow: async (callback: OverrideCallback) => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeOnBuyNow;
          productPageStore.setOnBuyNowCallback(callback);
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        },
        setBuyNowLabel: async (text: string) => {
          await waitUntilReady;
          const panoramaInteraction = ProductPagePanoramaTransactions.WixCodeOnBuyNow;
          productPageStore.setBuyNowTextOverride(text);
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        },
      }),
    });

  const updateConfig = (_$w: I$WWrapper, newConfig: IProductPageControllerConfig) => {
    config = _.clone(newConfig);
    return productPageStore.updateState(config);
  };

  const updateAppSettings = (_$w: I$WWrapper, updates: {[key: string]: any}) => {
    /* istanbul ignore else */
    if (updates.scope === Scope.COMPONENT && updates.source === 'app-settings') {
      productPageStore.updateAppSettings(updates.payload);
    }
  };

  const load = async () => {
    const {publicData} = config;
    if (productPageStore) {
      return;
    }

    productPageStore = new ProductPageStore(
      config,
      controllerParams.dangerousPublicDataOverride,
      publicData,
      setProps,
      siteStore,
      controllerConfigRef.config.externalId || '',
      flowAPI.reportError,
      controllerConfigRef.compId,
      createSlotVeloAPIFactory(controllerConfigRef),
      controllerConfigRef,
      flowAPI.errorHandler,
      flowAPI.panoramaClient
    );

    await productPageStore
      .setInitialState()
      .then(() => {
        onReadyResolver();
      })
      .catch(flowAPI.reportError);
  };

  const init = async () => {
    await siteStore.loadClientConfig();
    controllerParams.controllerConfig.wixCodeApi.location.onChange(() => void load());
    await load();
  };

  return {
    async pageReady() {
      await init();
    },
    load,
    exports,
    updateConfig,
    updateAppSettings,
  };
};
